
import React from 'react';
import { X, AlertTriangle } from 'lucide-react';

interface ConfirmModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    isDangerous?: boolean;
    /**
     * Blocks the confirm action while keeping the modal open so the `message`
     * can explain WHY. Added for role deletion: a role still assigned to
     * members must not be deletable, and silently hiding the button would read
     * as a broken dialog. Optional and defaulted, so every existing caller is
     * unchanged.
     */
    disableConfirm?: boolean;
    /**
     * Optional extra controls rendered under the message (e.g. an "Also remove
     * the identity key" checkbox on Spago disconnect). The caller owns their
     * state and reads it in `onConfirm`. Optional, so every existing caller is
     * unchanged.
     */
    children?: React.ReactNode;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    isDangerous = false,
    disableConfirm = false,
    children,
}) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full p-6 relative animate-fade-in">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                    <X className="w-5 h-5" />
                </button>

                <div className="flex items-start space-x-4">
                    <div className={`p-3 rounded-full ${isDangerous ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'}`}>
                        <AlertTriangle className="w-6 h-6" />
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                            {title}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-300 mb-6">
                            {message}
                        </p>
                        {children && <div className="-mt-3 mb-6">{children}</div>}
                        <div className="flex space-x-3 justify-end">
                            <button
                                onClick={onClose}
                                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                            >
                                {cancelText}
                            </button>
                            <button
                                onClick={() => {
                                    if (disableConfirm) return;
                                    onConfirm();
                                    onClose();
                                }}
                                disabled={disableConfirm}
                                className={`px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isDangerous
                                        ? 'bg-red-600 hover:bg-red-700'
                                        : 'bg-primary hover:bg-primary-600'
                                    }`}
                            >
                                {confirmText}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConfirmModal;
